import { Play, Bookmark, BookOpen, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import useQuranStore from '../store/quranStore';

export default function VerseItem({ 
  verse, 
  ayahNo, 
  surahNo, 
  surahName,
  onPlayAudio,
  onShowTafsir,
  onStartMemorization,
  isCurrentlyPlaying 
}) {
  const { 
    translationLang, 
    showDiacritics, 
    fontSize,
    isBookmarked, 
    addBookmark, 
    removeBookmark,
    getMemorizationStatus,
  } = useQuranStore();

  // Get the correct Arabic text based on settings
  const arabicText = showDiacritics ? verse.arabic1 : verse.arabic2;
  
  // Get translation based on selected language
  const getTranslation = () => {
    switch (translationLang) {
      case 'urdu': return verse.urdu;
      case 'bengali': return verse.bengali;
      case 'turkish': return verse.turkish;
      case 'uzbek': return verse.uzbek;
      default: return verse.english;
    }
  };

  const translation = getTranslation();
  const bookmarked = isBookmarked(surahNo, ayahNo);
  const memStatus = getMemorizationStatus(surahNo, ayahNo);

  const handleBookmark = () => {
    if (bookmarked) {
      removeBookmark(surahNo, ayahNo);
    } else {
      addBookmark({
        surahNo,
        ayahNo,
        surahName,
        arabic: arabicText,
        english: verse.english,
      });
    }
  };

  // Font size multiplier
  const arabicSize = `${2 + (fontSize - 1) * 0.5}rem`;
  const translationSize = `${1 + (fontSize - 1) * 0.125}rem`;
  
  // Get memorization status styling
  const getMemStatusStyle = () => {
    if (memStatus.status === 'memorized') return 'text-green-600 border-green-600';
    if (memStatus.status === 'review') return 'text-amber-600 border-amber-600';
    if (memStatus.repetitions > 0) return 'text-blue-600 border-blue-600';
    return '';
  };

  return (
    <div
      className={`verse-item verse-divider py-8 transition-colors ${isCurrentlyPlaying ? 'bg-primary/5 border-l-2 border-primary pl-4' : ''}`}
      data-testid={`verse-${surahNo}-${ayahNo}`}
    >
      {/* Arabic text */}
      <div className="arabic-text mb-6" style={{ fontSize: arabicSize }}>
        <span className="verse-number mr-3">{ayahNo}</span>
        {arabicText}
      </div>

      {/* Translation */}
      <p 
        className="text-muted-foreground leading-relaxed"
        style={{ fontSize: translationSize }}
      >
        {translation}
      </p>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2 mt-4">
        <Button
          variant={isCurrentlyPlaying ? 'default' : 'outline'}
          size="sm"
          onClick={() => onPlayAudio && onPlayAudio(ayahNo)}
          className="gap-2"
          data-testid={`play-verse-${ayahNo}`}
        >
          <Play className="w-3 h-3" />
          {isCurrentlyPlaying ? 'Playing' : 'Play'}
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={handleBookmark}
          className={`gap-2 ${bookmarked ? 'text-primary border-primary' : ''}`}
          data-testid={`bookmark-verse-${ayahNo}`}
        >
          <Bookmark className={`w-3 h-3 ${bookmarked ? 'fill-current' : ''}`} />
          {bookmarked ? 'Saved' : 'Save'}
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => onShowTafsir && onShowTafsir(ayahNo)}
          className="gap-2"
          data-testid={`tafsir-verse-${ayahNo}`}
        >
          <BookOpen className="w-3 h-3" />
          Tafsir
        </Button>
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => onStartMemorization && onStartMemorization(ayahNo)}
          className={`gap-2 ${getMemStatusStyle()}`}
          data-testid={`memorize-verse-${ayahNo}`}
        >
          <RotateCcw className="w-3 h-3" />
          {memStatus.repetitions > 0 ? `Memorize (${memStatus.repetitions})` : 'Memorize'}
        </Button>
      </div>
    </div>
  );
}
